import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { useCookies } from "react-cookie";
import {
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Icon,
  IconButton,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  Stack,
  Tag,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import {
  FiArrowRight,
  FiCamera,
  FiCheck,
  FiFileText,
  FiLock,
  FiPercent,
  FiSearch,
  FiShoppingBag,
  FiShoppingCart,
  FiTrash2,
  FiTruck,
  FiUpload,
  FiStar,
  FiShield,
  FiPlus,
} from "react-icons/fi";
import Sidebar from "../Pages/side";
import WithSubnavigation from "../Pages/navbar";
import { colors, gradients, radii, shadows } from "../UI/designSystem";
import {
  AnimStyles,
  CategoryPill,
  EmptyState,
  MedicineImage,
  PriceTag,
  QuantityStepper,
  SocialProof,
  TrustStrip,
} from "../UI/Primitives";

import { API_BASE } from "../../config";
import { performLogout } from "../../utils/logout";
const FREE_DELIVERY_THRESHOLD = 299;

const Cart = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toastUI = useToast();
  const [, , removeCookie] = useCookies(["token", "role"]);

  const [cart, setCart] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedPrescriptionPreview, setSelectedPrescriptionPreview] = useState(null);
  const [prescriptionMeta, setPrescriptionMeta] = useState(null);
  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [username, setUsername] = useState("");

  useEffect(() => {
    const getCart = async () => {
      try {
        const response = await axios.get(`${API_BASE}/cart`, { withCredentials: true });
        setCart(response.data);
        if (response.data?.prescriptionPreview) setSelectedPrescriptionPreview(response.data.prescriptionPreview);
      } catch {}
    };
    const savedPreview = localStorage.getItem("prescriptionDraft");
    if (savedPreview) {
      try {
        const parsed = JSON.parse(savedPreview);
        setSelectedPrescriptionPreview(parsed.previewUrl || null);
        setPrescriptionMeta({
          name: parsed.filename || "Prescription",
          address: parsed.address,
        });
      } catch {}
    }
    if (location.state && location.state.fromPrescription) {
      setPrescriptionMeta((prev) => ({
        ...prev,
        pharmacy: location.state.pharmacy || prev?.pharmacy,
        address: location.state.address || prev?.address,
      }));
    }
    getCart();

    (async () => {
      try {
        const { data } = await axios.post(`${API_BASE}`, {}, { withCredentials: true });
        if (data?.user) setUsername(data.user);
      } catch {}
    })();
  }, [location.state]);

  const totals = useMemo(() => {
    const subtotal = cart?.bill || 0;
    const baseDiscount = cart?.discount ? cart.discount * subtotal : 0;
    const genericSavings = Math.round(subtotal * 0.08);
    const promoDiscount = promoApplied ? Math.min(subtotal * 0.1, 100) : 0;
    const deliveryFee = subtotal >= FREE_DELIVERY_THRESHOLD ? 0 : 29;
    const tax = Math.round((subtotal - baseDiscount - promoDiscount) * 0.05);
    const total = Math.max(0, subtotal - baseDiscount - promoDiscount + deliveryFee + tax);
    return { subtotal, baseDiscount, promoDiscount, genericSavings, deliveryFee, tax, total };
  }, [cart, promoApplied]);

  const progressToFreeDelivery = Math.min(100, Math.round((totals.subtotal / FREE_DELIVERY_THRESHOLD) * 100));

  const handleQuantityChange = async (productId, newQuantity) => {
    if (newQuantity < 1) return;
    try {
      const response = await axios.post(
        `${API_BASE}/cart/update`,
        { productId, quantity: newQuantity },
        { withCredentials: true },
      );
      setCart(response.data);
    } catch {}
  };

  const handleDelete = async (productId) => {
    try {
      const response = await axios.delete(`${API_BASE}/cart/${productId}`, { withCredentials: true });
      if (response.status === 200) setCart(response.data);
    } catch {}
  };

  const handleSearchChange = async (val) => {
    setSearchTerm(val);
    if (!val || val.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    setIsSearching(true);
    try {
      const resp = await axios.get(`${API_BASE}/api/medicines/by-name`, {
        params: { name: val.trim(), limit: 8, namesOnly: false },
      });
      setSearchResults(resp.data?.results || []);
    } catch {
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const addMedicineToCart = async (productId) => {
    if (!productId) return;
    try {
      const resp = await axios.post(
        `${API_BASE}/cart/add`,
        { productId, quantity: 1 },
        { withCredentials: true },
      );
      setCart(resp.data);
      toastUI({ title: "Added", status: "success", duration: 1400, position: "bottom-right", isClosable: true });
    } catch (err) {
      toastUI({
        title: err?.response?.data || "Could not add medicine",
        status: "error",
        duration: 2200,
        isClosable: true,
        position: "bottom-right",
      });
    }
  };

  const applyPromo = () => {
    if (!promoCode.trim()) return;
    const code = promoCode.trim().toUpperCase();
    if (["FIRST10", "HEALTH10", "WELCOME"].includes(code)) {
      setPromoApplied(code);
      toastUI({ title: `Code ${code} applied`, status: "success", duration: 1800, position: "bottom-right" });
    } else {
      toastUI({ title: "Invalid code", description: "Try FIRST10 or HEALTH10", status: "error", duration: 2200, position: "bottom-right" });
    }
  };

  const handleCheckout = () => {
    toastUI({
      title: "Proceeding to checkout",
      description: "Checkout flow is wired into PrescriptionOrderSummary.",
      status: "info",
      duration: 1800,
      isClosable: true,
      position: "bottom-right",
    });
  };

  const logout = () => performLogout(removeCookie);

  const items = cart?.items || [];
  const hasItems = items.length > 0;

  return (
    <>
      <AnimStyles />
      <Sidebar
        onLogout={logout}
        collapsed={sidebarCollapsed}
        onToggleSidebar={() => setSidebarCollapsed((p) => !p)}
        brandName="PillStack"
        username={username}
        chatLink={username ? `/chatwithdoctor/${username}` : null}
      />
      <Box position="fixed" top={0} left={0} right={0} zIndex={1000}>
        <WithSubnavigation
          username={username}
          onLogout={logout}
          notifications={[]}
          openChangePasswordModal={() => {}}
          onToggleSidebar={() => setSidebarCollapsed((p) => !p)}
          collapsed={sidebarCollapsed}
          chatLink={username ? `/chatwithdoctor/${username}` : null}
          isLoggedIn={Boolean(username)}
        />
      </Box>
      <Box
        bg={colors.neutral[50]}
        minH="100vh"
        pt="90px"
        pb={{ base: "120px", md: "40px" }}
        px={{ base: 4, md: 6 }}
        ml={sidebarCollapsed ? "74px" : "248px"}
        transition="margin-left 0.3s ease"
      >
        <Box maxW="1400px" mx="auto">
          {/* Header */}
          <Flex align="center" justify="space-between" wrap="wrap" gap={3} mb={5}>
            <Box>
              <Text fontSize="11px" fontWeight="800" color={colors.brand[600]} textTransform="uppercase" letterSpacing="0.12em" m={0}>
                Cart
              </Text>
              <Text fontSize="28px" fontWeight="800" color={colors.neutral[900]} m={0} letterSpacing="-0.02em">
                {hasItems ? "Review your order" : "Your cart is empty"}
              </Text>
              <Text color={colors.neutral[500]} fontSize="14px" mt={1} m={0}>
                {hasItems ? `${items.length} medicine${items.length > 1 ? "s" : ""} ready for checkout` : "Upload a prescription or browse medicines to get started"}
              </Text>
            </Box>
            {hasItems ? (
              <Tag
                bg={colors.brand[50]}
                color={colors.brand[700]}
                border={`1px solid ${colors.brand[100]}`}
                borderRadius="full"
                px={3}
                py={2}
                fontSize="12px"
                fontWeight="800"
              >
                Saving ₹{totals.genericSavings} with generics
              </Tag>
            ) : null}
          </Flex>

          {!hasItems ? (
            <Stack spacing={5}>
              <EmptyState
                icon={FiShoppingCart}
                title="Your cart is empty"
                subtitle="Upload a prescription or browse medicines to start placing your order. Our AI will read your Rx in 3 seconds and add the right medicines automatically."
                primaryAction={{
                  label: "Upload prescription",
                  onClick: () => navigate("/upload-prescription"),
                  icon: FiUpload,
                }}
                secondaryAction={{ label: "Browse medicines", onClick: () => navigate("/medicine") }}
              />
              <Flex justify="center">
                <TrustStrip />
              </Flex>
            </Stack>
          ) : (
            <Flex direction={{ base: "column", lg: "row" }} gap={6} align="flex-start">
              {/* LEFT: Items + search */}
              <Box flex="2" minW={0}>
                {/* Free-delivery progress */}
                {totals.subtotal < FREE_DELIVERY_THRESHOLD ? (
                  <Box
                    bg={colors.warning[50]}
                    border={`1px solid ${colors.warning[100]}`}
                    borderRadius={radii.md}
                    p={4}
                    mb={4}
                  >
                    <HStack justify="space-between" mb={2}>
                      <HStack color={colors.warning[700]} spacing={2}>
                        <Icon as={FiTruck} />
                        <Text fontSize="13px" fontWeight="800" m={0}>
                          Add ₹{FREE_DELIVERY_THRESHOLD - totals.subtotal} more for free delivery
                        </Text>
                      </HStack>
                      <Text fontSize="11px" fontWeight="800" color={colors.warning[700]} m={0}>
                        {progressToFreeDelivery}%
                      </Text>
                    </HStack>
                    <Box h="8px" bg={colors.warning[100]} borderRadius="full" overflow="hidden">
                      <Box
                        h="100%"
                        w={`${progressToFreeDelivery}%`}
                        bgGradient={`linear-gradient(90deg, ${colors.warning[500]}, ${colors.accent[500]})`}
                        transition="width 0.4s ease"
                      />
                    </Box>
                  </Box>
                ) : (
                  <Box
                    bg={colors.success[50]}
                    border={`1px solid ${colors.success[100]}`}
                    borderRadius={radii.md}
                    p={3}
                    mb={4}
                  >
                    <HStack color={colors.success[700]} spacing={2}>
                      <Icon as={FiCheck} />
                      <Text fontSize="13px" fontWeight="800" m={0}>
                        You qualify for FREE delivery
                      </Text>
                    </HStack>
                  </Box>
                )}

                {/* Prescription preview */}
                {selectedPrescriptionPreview ? (
                  <Box
                    bg="white"
                    border={`1px solid ${colors.neutral[200]}`}
                    borderRadius={radii.md}
                    p={4}
                    mb={4}
                    boxShadow={shadows.xs}
                  >
                    <HStack spacing={4} align="flex-start">
                      <Image
                        src={selectedPrescriptionPreview}
                        alt={prescriptionMeta?.name || "Prescription"}
                        borderRadius={radii.sm}
                        w="96px"
                        h="96px"
                        objectFit="cover"
                        bg={colors.neutral[50]}
                        border={`1px solid ${colors.neutral[200]}`}
                      />
                      <Box flex="1">
                        <HStack spacing={2} color={colors.brand[700]} mb={1}>
                          <Icon as={FiFileText} />
                          <Text fontSize="11px" fontWeight="800" textTransform="uppercase" letterSpacing="0.08em" m={0}>
                            Prescription attached
                          </Text>
                        </HStack>
                        <Text fontWeight="800" color={colors.neutral[900]} m={0}>
                          {prescriptionMeta?.name || "Prescription"}
                        </Text>
                        {prescriptionMeta?.pharmacy ? (
                          <Text fontSize="12px" color={colors.neutral[500]} m={0} mt={0.5}>
                            Pharmacy: {prescriptionMeta.pharmacy}
                          </Text>
                        ) : null}
                        <Tag
                          mt={2}
                          bg={colors.info[50]}
                          color={colors.info[700]}
                          border={`1px solid ${colors.info[100]}`}
                          borderRadius="full"
                          fontSize="11px"
                          fontWeight="800"
                        >
                          Will be validated by pharmacist
                        </Tag>
                      </Box>
                    </HStack>
                  </Box>
                ) : null}

                {/* Add medicines search */}
                <Box
                  bg="white"
                  border={`1px solid ${colors.neutral[200]}`}
                  borderLeft={`4px solid ${colors.brand[500]}`}
                  borderRadius={radii.md}
                  p={4}
                  mb={4}
                  boxShadow={shadows.xs}
                >
                  <HStack justify="space-between" mb={3}>
                    <Text fontWeight="800" color={colors.neutral[900]} m={0} fontSize="14px">
                      Need anything else?
                    </Text>
                    <Tag colorScheme="teal" borderRadius="full" fontSize="10px" fontWeight="800" textTransform="uppercase">
                      AI search
                    </Tag>
                  </HStack>
                  <InputGroup size="lg">
                    <InputLeftElement pointerEvents="none" h="48px">
                      <Icon as={FiSearch} color={colors.neutral[400]} />
                    </InputLeftElement>
                    <Input
                      placeholder="Search any medicine, e.g. Dolo 650 or Paracetamol"
                      value={searchTerm}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      bg="transparent"
                      border={`1px solid ${colors.neutral[200]}`}
                      h="48px"
                      fontSize="15px"
                      _focus={{ borderColor: colors.brand[400], boxShadow: `0 0 0 1px ${colors.brand[400]}` }}
                    />
                  </InputGroup>
                  {isSearching ? (
                    <Text fontSize="12px" color={colors.neutral[500]} mt={2} m={0}>Searching…</Text>
                  ) : null}
                  {!isSearching && searchResults?.length > 0 ? (
                    <Stack mt={3} spacing={2}>
                      {searchResults.map((med) => (
                        <Flex
                          key={med._id || med.id || med.product_id || med.productId}
                          align="center"
                          gap={3}
                          border={`1px solid ${colors.neutral[200]}`}
                          borderRadius={radii.sm}
                          p={2.5}
                          bg="white"
                          _hover={{ borderColor: colors.brand[200], boxShadow: shadows.xs }}
                          transition="all 0.18s ease"
                        >
                          <MedicineImage med={med} size={48} rounded="sm" />
                          <Box flex="1" minW={0}>
                            <Text fontWeight="800" color={colors.neutral[900]} m={0} fontSize="14px" noOfLines={1}>
                              {med.product_name || med.Name || med.name}
                            </Text>
                            <HStack spacing={2} mt={1}>
                              <CategoryPill med={med} />
                              <Text fontSize="11px" color={colors.neutral[500]} m={0} noOfLines={1}>
                                {med.composition || med.key_ingredients || "—"}
                              </Text>
                            </HStack>
                          </Box>
                          <Button
                            size="sm"
                            leftIcon={<FiPlus />}
                            bgGradient={gradients.brand}
                            color="white"
                            _hover={{ bgGradient: gradients.brandBold }}
                            onClick={() => addMedicineToCart(med._id || med.id || med.product_id)}
                          >
                            Add
                          </Button>
                        </Flex>
                      ))}
                    </Stack>
                  ) : null}
                </Box>

                {/* Item list */}
                <Stack spacing={3}>
                  {items.map((item, index) => (
                    <Box
                      key={index}
                      bg="white"
                      border={`1px solid ${colors.neutral[200]}`}
                      borderRadius={radii.md}
                      p={4}
                      boxShadow={shadows.xs}
                      _hover={{ borderColor: colors.brand[200], boxShadow: shadows.sm }}
                      transition="all 0.18s ease"
                    >
                      <Flex align="flex-start" gap={4}>
                        <MedicineImage
                          med={{ ...item, image: item.image, Image: item.image, Name: item.name, product_name: item.name }}
                          size={88}
                          rounded="md"
                        />
                        <Flex flex="1" direction="column" gap={1.5}>
                          <Text fontWeight="800" color={colors.neutral[900]} m={0} fontSize="15px" noOfLines={1}>
                            {item.name}
                          </Text>
                          <CategoryPill med={{ product_name: item.name, composition: item.composition }} />
                          <Text fontSize="12px" color={colors.neutral[500]} m={0}>
                            ₹{Number(item.price).toFixed(0)} each
                          </Text>
                          <HStack spacing={3} mt={1}>
                            <QuantityStepper
                              value={item.quantity}
                              onChange={(v) => handleQuantityChange(item.productId, v)}
                            />
                            <Button
                              variant="ghost"
                              color={colors.error[600]}
                              size="sm"
                              leftIcon={<FiTrash2 />}
                              onClick={() => handleDelete(item.productId)}
                              _hover={{ bg: colors.error[50] }}
                            >
                              Remove
                            </Button>
                          </HStack>
                        </Flex>
                        <Box textAlign="right">
                          <PriceTag price={item.price * item.quantity} mrp={item.mrp ? item.mrp * item.quantity : undefined} size="lg" />
                        </Box>
                      </Flex>
                    </Box>
                  ))}
                </Stack>

                <Box mt={4}>
                  <TrustStrip />
                </Box>
              </Box>

              {/* RIGHT: Summary */}
              <Box
                w={{ base: "100%", lg: "380px" }}
                flexShrink={0}
                position={{ base: "static", lg: "sticky" }}
                top={{ lg: "100px" }}
              >
                <Box
                  bg="white"
                  border={`1px solid ${colors.neutral[200]}`}
                  borderRadius={radii.lg}
                  p={6}
                  boxShadow={shadows.md}
                >
                  <Text fontSize="18px" fontWeight="800" color={colors.neutral[900]} m={0} mb={4} letterSpacing="-0.01em">
                    Order summary
                  </Text>

                  {/* Promo */}
                  <Box mb={4}>
                    <Text fontSize="11px" fontWeight="800" color={colors.neutral[500]} textTransform="uppercase" letterSpacing="0.08em" m={0} mb={1.5}>
                      Promo code
                    </Text>
                    <HStack spacing={2}>
                      <InputGroup size="sm">
                        <InputLeftElement pointerEvents="none">
                          <Icon as={FiPercent} color={colors.neutral[400]} />
                        </InputLeftElement>
                        <Input
                          placeholder="Try FIRST10"
                          value={promoCode}
                          onChange={(e) => setPromoCode(e.target.value)}
                          borderColor={colors.neutral[200]}
                          isDisabled={Boolean(promoApplied)}
                        />
                      </InputGroup>
                      {promoApplied ? (
                        <Button
                          size="sm"
                          variant="outline"
                          color={colors.error[600]}
                          onClick={() => { setPromoApplied(null); setPromoCode(""); }}
                        >
                          Remove
                        </Button>
                      ) : (
                        <Button size="sm" colorScheme="teal" onClick={applyPromo}>
                          Apply
                        </Button>
                      )}
                    </HStack>
                    {promoApplied ? (
                      <HStack mt={2} color={colors.success[700]} spacing={1}>
                        <Icon as={FiCheck} />
                        <Text fontSize="11px" fontWeight="800" m={0}>
                          {promoApplied} applied — you save ₹{Math.round(totals.promoDiscount)}
                        </Text>
                      </HStack>
                    ) : null}
                  </Box>

                  {/* Loyalty points */}
                  <Box bg={colors.brand[50]} border={`1px solid ${colors.brand[100]}`} borderRadius={radii.sm} p={3} mb={4}>
                    <HStack justify="space-between">
                      <HStack color={colors.brand[700]} spacing={2}>
                        <Icon as={FiStar} />
                        <Text fontSize="12px" fontWeight="800" m={0}>
                          120 PillPoints available
                        </Text>
                      </HStack>
                      <Button size="xs" variant="ghost" color={colors.brand[700]} fontWeight="800" _hover={{ bg: colors.brand[100] }}>
                        Use ₹12 off
                      </Button>
                    </HStack>
                  </Box>

                  <Stack spacing={2.5}>
                    <Flex justify="space-between">
                      <Text color={colors.neutral[500]} m={0}>Subtotal</Text>
                      <Text fontWeight="700" m={0}>₹{totals.subtotal.toFixed(0)}</Text>
                    </Flex>
                    {totals.baseDiscount > 0 ? (
                      <Flex justify="space-between">
                        <Text color={colors.neutral[500]} m={0}>Discount</Text>
                        <Text fontWeight="700" color={colors.success[700]} m={0}>−₹{totals.baseDiscount.toFixed(0)}</Text>
                      </Flex>
                    ) : null}
                    {promoApplied ? (
                      <Flex justify="space-between">
                        <Text color={colors.neutral[500]} m={0}>Promo ({promoApplied})</Text>
                        <Text fontWeight="700" color={colors.success[700]} m={0}>−₹{Math.round(totals.promoDiscount)}</Text>
                      </Flex>
                    ) : null}
                    <Flex justify="space-between">
                      <Text color={colors.neutral[500]} m={0}>Delivery</Text>
                      <Text fontWeight="700" color={totals.deliveryFee === 0 ? colors.success[700] : colors.neutral[900]} m={0}>
                        {totals.deliveryFee === 0 ? "FREE" : `₹${totals.deliveryFee}`}
                      </Text>
                    </Flex>
                    <Flex justify="space-between">
                      <Text color={colors.neutral[500]} m={0}>Taxes & fees</Text>
                      <Text fontWeight="700" m={0}>₹{totals.tax}</Text>
                    </Flex>
                    <Divider my={1} />
                    <Flex justify="space-between" align="center">
                      <Text fontWeight="800" color={colors.neutral[900]} fontSize="16px" m={0}>
                        To pay
                      </Text>
                      <Text fontWeight="900" color={colors.brand[700]} fontSize="22px" m={0} letterSpacing="-0.02em">
                        ₹{totals.total.toFixed(0)}
                      </Text>
                    </Flex>
                  </Stack>

                  <Button
                    mt={5}
                    w="full"
                    size="lg"
                    bgGradient={gradients.brand}
                    color="white"
                    fontWeight="800"
                    rightIcon={<FiArrowRight />}
                    _hover={{ bgGradient: gradients.brandBold, transform: "translateY(-1px)", boxShadow: shadows.brandHover }}
                    onClick={handleCheckout}
                  >
                    Check prices from nearby pharmacies
                  </Button>

                  <HStack spacing={4} mt={3} justify="center" color={colors.neutral[500]}>
                    <HStack spacing={1}>
                      <Icon as={FiLock} boxSize={3} />
                      <Text fontSize="11px" fontWeight="700" m={0}>SSL secured</Text>
                    </HStack>
                    <HStack spacing={1}>
                      <Icon as={FiShield} boxSize={3} color={colors.brand[600]} />
                      <Text fontSize="11px" fontWeight="700" m={0}>Licensed pharmacies</Text>
                    </HStack>
                  </HStack>
                </Box>
                <Box mt={3}>
                  <SocialProof />
                </Box>
              </Box>
            </Flex>
          )}
        </Box>
      </Box>

      {/* Sticky mobile checkout bar */}
      {hasItems ? (
        <Box
          position="fixed"
          bottom={0}
          left={0}
          right={0}
          bg="white"
          borderTop={`1px solid ${colors.neutral[200]}`}
          boxShadow="0 -10px 24px rgba(15,23,42,0.08)"
          p={3}
          display={{ base: "block", lg: "none" }}
          zIndex={900}
        >
          <Flex align="center" gap={3}>
            <Box flex="1">
              <Text fontSize="11px" color={colors.neutral[500]} m={0}>To pay</Text>
              <Text fontSize="20px" fontWeight="900" color={colors.brand[700]} m={0}>₹{totals.total.toFixed(0)}</Text>
            </Box>
            <Button
              size="lg"
              bgGradient={gradients.brand}
              color="white"
              fontWeight="800"
              rightIcon={<FiArrowRight />}
              _hover={{ bgGradient: gradients.brandBold }}
              onClick={handleCheckout}
            >
              Check prices
            </Button>
          </Flex>
        </Box>
      ) : null}
    </>
  );
};

export default Cart;
